// ============================================================
// GitLogHistory.jsx
//
// A branded, responsive course content page for the
// "Git Log & History" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Commit auditing, diff comparison, and
//           line-by-line authorship (blame) logic.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import HistoryIcon            from "@mui/icons-material/History";
import VisibilityIcon         from "@mui/icons-material/Visibility";
import CompareArrowsIcon      from "@mui/icons-material/CompareArrows";
import GavelIcon              from "@mui/icons-material/Gavel";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import TimelineIcon           from "@mui/icons-material/Timeline";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitLogHistory() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<HistoryIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Auditing & Inspection"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git <span>History & Inspection</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Navigate through time. Git keeps a meticulous record of every change, allowing you to audit authorship, compare code versions, and visualize the evolution of your project branches.
                    </Typography>
                </Box>

                {/* 1. VIEWING LOGS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TimelineIcon />} label="1. The Commit Timeline" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        The <code>log</code> command is your primary tool for history inspection. Use flags to filter and format the output for better readability.
                    </Typography>
                    <CodeBlock
                        title="Log Variations"
                        code={`# Basic history\ngit log\n\n# Compact, one-line view\ngit log --oneline\n\n# Visual graph with branch decorators\ngit log --graph --all --decorate`}
                    />
                </Box>

                {/* 2. INSPECTION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VisibilityIcon />} label="2. Inspecting Commits" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Want to see exactly what changed in a specific snapshot? <code>git show</code> expands a commit hash into a full diff.
                    </Typography>
                    <CodeBlock
                        title="Show Metadata & Diffs"
                        code={`# Show last commit\ngit show\n\n# Show specific commit by hash\ngit show a1b2c3d\n\n# Show changes in a specific file from a commit\ngit show a1b2c3d:path/to/file.js`}
                    />
                </Box>

                {/* 3. DIFFERENCES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CompareArrowsIcon />} label="3. Comparing Changes" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Understand the "delta" between your working directory, the staging area, and previous commits.
                    </Typography>
                    <CodeBlock
                        title="Diff Logic"
                        code={`# Unstaged changes vs. Local files\ngit diff\n\n# Staged changes vs. Last commit\ngit diff --staged\n\n# Compare two different commits\ngit diff HEAD~1 HEAD`}
                    />
                </Box>

                {/* 4. BLAME */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<GavelIcon />} label="4. Line-by-Line Authorship" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use <code>blame</code> to see who last modified each line of a file. It’s the ultimate tool for tracking down the origin of a bug or understanding legacy logic.
                    </Typography>
                    <CodeBlock
                        title="Blame & Annotate"
                        code={`# Show authorship for every line\ngit blame app.js\n\n# Limit blame to specific lines\ngit blame -L 10,20 app.js`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="History Auditing Checklist" />
                    <Grid container spacing={1.5}>
                        {[
                            "Use 'git log --oneline' for a quick pulse-check of recent work.",
                            "Run 'git diff' before adding files to verify what you're staging.",
                            "Use 'git show' to review your own work before pushing it to remote.",
                            "Leverage 'git blame' to find the right person to ask about complex code.",
                            "Filter logs by author using 'git log --author=\"Name\"'.",
                            "Search commit messages for keywords using 'git log --grep=\"feature\"'."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Want a beautiful history view? Try <code>git log --pretty=format:"%h %ad | %s%d [%an]" --graph --date=short</code>. It creates a clean, colored timeline with dates and author names!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
